import { useMemo } from 'react';
import {
  computeValuationRegression, signedCorrelation, TIME_HORIZONS, LOOKBACK_WINDOWS,
} from '../utils/valuationRegression';
import { METRIC_OPTIONS } from '../config/metricsConfig';

const HORIZONS = Object.keys(TIME_HORIZONS); // ['2Y','5Y','10Y','15Y']

/**
 * One row per (metric, fixed horizon) combination — the correlation table's
 * unit of display.
 * @typedef {Object} CorrelationRow
 * @property {'eps'|'revenue'|'ebitda'|'ebit'|'netIncome'|'fcf'|'ocf'|'dividends'} metric
 * @property {string} metricLabel - METRIC_OPTIONS' human-readable label
 * @property {'2Y'|'5Y'|'10Y'|'15Y'} horizon
 * @property {number} rSquared - fit strength, `[0, 1]`, magnitude only
 * @property {number} correlation - signed `r = sign(slope) * sqrt(rSquared)`
 * @property {boolean} isInsufficientData - too few valid backtest pairs
 *   (see `MIN_VALID_PAIRS`) to trust this row's `rSquared`/`correlation`
 */

/**
 * Runs `computeValuationRegression` for ALL 8 `METRIC_OPTIONS` x the 4
 * fixed `TIME_HORIZONS` (32 rows total) — the "which fundamental metric has
 * historically been the best predictor of this stock's subsequent returns"
 * summary table, as opposed to `useValuationRegression`'s single
 * currently-selected metric x all horizons (chart-driving) results. This
 * reuses that exact same calculation, just swept across every metric too,
 * rather than inventing a second methodology.
 *
 * Covers the 4 fixed holding-period horizons (2Y/5Y/10Y/15Y) across every
 * metric — an apples-to-apples comparison, since every row uses the same
 * fixed horizon lengths rather than a per-metric adaptive one.
 *
 * Sorted descending by `rSquared` (equivalently, descending `|correlation|`
 * — "best predictor" means strongest fit regardless of direction), with
 * every `isInsufficientData` row pushed to the bottom regardless of its own
 * (still-computed, but statistically unreliable) `rSquared` — a low-history
 * combination should never appear to outrank a well-supported one just
 * because its few points happened to line up.
 *
 * Recomputes (all 32 rows) whenever the raw series' *references* change
 * (new fetch/new symbol) OR `lookback`/`period`/`valueBasis` changes — the
 * same selections `useValuationRegression`'s chart uses, so the table's
 * "best predictor" ranking reflects the same evaluation window, multiple
 * vintage, and value basis the user is currently looking at rather than
 * always sweeping full history on a fixed NTM/Market Cap basis regardless of
 * them. `price`, `shares`, `epsQuarterly`, `metricSeriesByMetric`,
 * `lookback`, `period`, `valueBasis` are the useMemo dependency array (minus
 * `metric`, since this table covers every metric at once rather than one at
 * a time).
 *
 * @param {Array<[number, number]>} price - raw price series, `[ts, price][]`
 * @param {Array<[number, number]>} shares - raw shares-outstanding series,
 *   `[ts, shares][]`
 * @param {Array<[number, number]>} epsQuarterly - raw quarterly EPS
 *   actuals, `[ts, eps][]`
 * @param {{[metric: string]: Array<[number, number]>}} metricSeriesByMetric -
 *   the backend's existing per-metric series (annual actuals + forward FY
 *   estimates mixed), keyed by metric — every entry `METRIC_OPTIONS` lists
 *   is read here, not just the currently-selected chart metric
 * @param {'10Y'|'15Y'|'20Y'|'25Y'|'30Y'|'All'} [lookback='All'] - the
 *   currently-selected evaluation window (see `useValuationRegression`),
 *   applied identically to all 32 rows
 * @param {'ntm'|'ttm'} [period='ntm'] - the currently-selected multiple
 *   vintage (see valuationRegression.js's module docstring `period`
 *   section), applied identically to all 32 rows so the table's ranking
 *   recomputes against NTM or TTM in lockstep with the chart above it (and
 *   the card's period toggle).
 * @param {'marketCap'|'enterpriseValue'} [valueBasis='marketCap'] - the
 *   currently-selected company value basis, applied identically to all 32
 *   rows so the table recomputes against Market Cap or Enterprise Value in
 *   lockstep with the chart above it (and the page's top-of-page toggle).
 * @param {Array<[number, number]>} [netDebt=[]] - raw annual net-debt series,
 *   used only under Enterprise Value (see `useValuationRegression`).
 * @returns {Array<CorrelationRow>|null} `null` until both `price` and
 *   `epsQuarterly` have at least one point (matching
 *   `useValuationRegression`'s own loading gate), so callers can render a
 *   loading placeholder.
 */
export const useMetricCorrelationTable = (
  price, shares, epsQuarterly, metricSeriesByMetric, lookback = 'All', period = 'ntm', valueBasis = 'marketCap', netDebt = [], enabled = true
) => useMemo(() => {
  // Skip the 32-regression sweep while the valuation card is collapsed —
  // `enabled` is that card's open state (see Analysis.js). `null` matches the
  // loading gate, and the card omits the table entirely for a null result.
  if (!enabled) return null;
  if (!price?.length || !epsQuarterly?.length) return null;
  const nowTs = Date.now();
  const lookbackYears = LOOKBACK_WINDOWS[lookback] ?? null;

  const rows = METRIC_OPTIONS.flatMap(({ value: metric, label: metricLabel }) => {
    const metricSeries = metricSeriesByMetric?.[metric] || [];
    const futureFyEstimates = metricSeries.filter(([ts]) => ts > nowTs);
    const inputs = { price, shares, netDebt, metric, epsQuarterly, metricSeries, futureFyEstimates, lookbackYears, nowTs, period, valueBasis };

    return HORIZONS.map((horizon) => {
      const result = computeValuationRegression({ ...inputs, horizon });
      return {
        metric,
        metricLabel,
        horizon,
        rSquared: result.rSquared,
        correlation: signedCorrelation(result),
        isInsufficientData: result.isInsufficientData,
      };
    });
  });

  return rows.sort((a, b) => {
    if (a.isInsufficientData !== b.isInsufficientData) return a.isInsufficientData ? 1 : -1;
    return b.rSquared - a.rSquared;
  });
}, [enabled, price, shares, netDebt, epsQuarterly, metricSeriesByMetric, lookback, period, valueBasis]);

export default useMetricCorrelationTable;
